import { useContext, useEffect, useState } from 'react';
import {
    Button,
    Col,
    Row,
    FormGroup,
    Label,
    Input,
    FormFeedback,
    FormText,
    Card,
    CardBody,
    Table,
} from 'reactstrap';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { useHistory, useParams, Link } from 'react-router-dom';
import {
    TextInput,
    PhoneInput,
    currencyConfig,
} from '~/shared/components/form/Inputs';
import states from '~/shared/states.json';
import { renderIcon } from '~/shared/components/icons';
import { CampaignContext, CampaignState } from '~/context/campaign';
import { fetchMyCampaign, postEvent, putEvent } from '../../services/campaign';
import { AiOutlineCloseCircle } from 'react-icons/ai';
import toast from 'react-hot-toast';
import { CheckboxInput } from '~/shared/components/form/Inputs';
import {
    Fundraiser,
    FundraiserListItem,
    MyCampaignResponse,
    Registration,
} from '~/types/campaign';
import { FundraiserImageUpload } from './FundraiserImageUpload';
import { FundraiserFlyerUpload } from './FundraiserFlyerUpload';
import { getFilename } from '~/services/helper';
import {
    fetchFundraiser,
    fetchRegistrationDetails,
} from '~/services/fundraisers';
import { fetchRegistrations } from '~/services/fundraisers';
import { AuthContext } from '~/context/auth';
import { AdminContext } from '~/context/admin';
import IntlCurrencyInput from 'react-intl-currency-input';

type DefaultEditFundraiserType = typeof defaultEditFundraiser;

const defaultEditFundraiser = {
    published: false,
    name: '',
    details: '',
    thank_you_message: '',
    external_link: '',
    is_oneday: true,
    is_multiple_day: false,
    event_date: '',
    event_end_date: '',
    event_time_text: '',
    time: '',
    is_virtual: false,
    event_url: '',
    location: '',
    address_1: '',
    address_2: '',
    city: '',
    state: '',
    zip_code: '',
    i_am_organizer: false,
    organizer_first_name: '',
    organizer_last_name: '',
    organizer_phone: '',
    organizer_email: '',
    requires_registration: false,
    registration_close_date: '',
    call_to_action: '',
    event_picture: '',
    flyer: '',
    // registration_questions: [],
    registration_question: '',
    registration_items: [],
    is_ongoing: false,
};

export const FundraiserForm = () => {
    const [{ user }, userDispatch] = useContext(AuthContext);
    const [loading, setLoading] = useState(false);
    const [showCampaignPhotoUpload, setShowCampaignPhotoUpload] =
        useState(false);
    const [showFlyerPhotoUpload, setShowFlyerPhotoUpload] = useState(false);
    const [{ campaign }, campaignDispatch]: [CampaignState, any] =
        useContext(CampaignContext);
    const [{ campaignId }, adminDispatch] = useContext(AdminContext);
    const history = useHistory();
    const { id } = useParams<{ id: string }>();
    const isEditing = !!id;
    const [fundraiserToEdit, setFundraiserToEdit] = useState<
        Fundraiser | undefined
    >(undefined);
    const {
        control,
        handleSubmit,
        reset,
        setValue,
        getValues,
        register,
        watch,
        formState: { errors, isSubmitting, dirtyFields, isValid },
    } = useForm({
        defaultValues: defaultEditFundraiser,
        mode: 'all',
    });
    const [registrations, setRegistrations] = useState<Registration[]>([]);
    const [registrationDetails, setRegistrationDetails] = useState<any[]>([]);

    console.log('errors: ', errors);
    const hasErrors = Object.keys(errors).length > 0;

    const {
        fields: itemFields,
        append: appendItem,
        remove: removeItem,
    } = useFieldArray({
        control,
        name: 'registration_items',
        keyName: '_id', // Prevent react-hook-form from overwriting the key
    });



    const getButtonText = () => {
        if (isSubmitting) {
            return 'Saving';
        }
        return 'Save';
    };

    const onRemoveItem = (item, index) => {
        // Check if registrationDetail items have this item
        // If they do, don't remove it
        const itemInUse = registrationDetails.some((detail) => {
            return detail.item.id === item.id;
        });
        if (itemInUse) {
            toast.error(
                "This registration item cannot be removed as it's already purchased by a registrant"
            );
            return;
        }

        if (!itemInUse) {
            removeItem(index);
        }
    };

    const published = watch('published');
    const is_oneday = watch('is_oneday');
    const is_multiple_day = watch('is_multiple_day');
    const is_virtual = watch('is_virtual');
    const i_am_organizer = watch('i_am_organizer');
    const requires_registration = watch('requires_registration');
    const is_ongoing = watch('is_ongoing');

    const hasFundraiserLength = is_oneday || is_multiple_day || is_ongoing;

    const eventsUrl = import.meta.env.VITE_EVENT_URL || '';

    useEffect(() => {
        if (!campaign) {
            getMyCampaign();
        } else {
            setLoading(false);
        }
    }, []);

    const getRegistrations = async () => {
        try {
            const resp = await fetchRegistrations(id, campaignId);
            setRegistrations(resp.registrations);
        } catch (e) {
            console.log('getRegistrations() e: ', e);
            // Show some error here
        }
    };

    const getRegistrationDetails = async () => {
        try {
            const resp = await fetchRegistrationDetails(id, campaignId);
            setRegistrationDetails(resp);
        } catch (e) {
            console.log('getRegistrationDetails() e: ', e);
            // Show some error here
        }
    };

    useEffect(() => {
        if (campaign && !loading) {
            setLoading(true);
            getRegistrations();
            getRegistrationDetails();
        }
    }, [campaign]);

    const onSubmit = async (data: any) => {
        toast.loading('Saving...');
        const payload = { ...data };
        if (!is_multiple_day) {
            payload.event_end_date = null;
        }
        if (is_ongoing) {
            // clear dates
            payload.event_date = null;
            payload.event_end_date = null;
        }

        if (isEditing) {
            // PUT to update the event
            payload.id = id;

            const dirtyFieldsArray = Object.keys(dirtyFields);

            if (!dirtyFieldsArray.includes('registration_items')) {
                delete payload.registration_items;
            }

            const updatedFundraiser = await putEvent(
                payload,
                campaign?.campaignDetail.campaign_id
            );
            toast.dismiss();
            if (updatedFundraiser.detail) {
                if (updatedFundraiser.detail === 'Invalid token.') {
                    localStorage.removeItem('token');
                    history.push('/login');
                    return;
                }
                toast.error(updatedFundraiser.detail);
            } else {
                toast.success(
                    <div>
                        Fundraiser updated successfully!{' '}
                        <a
                            href={`${eventsUrl}/event/${fundraiserToEdit?.id}/signup/`}
                            target='_blank'
                        >
                            View your Campaign Fundraiser here!
                        </a>
                    </div>
                );
                // Scroll to top of page
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } else {
            const newFundraiser = await postEvent(
                payload,
                campaign?.campaignDetail.campaign_id
            );
            toast.dismiss();
            if (newFundraiser.detail) {
                if (newFundraiser.detail === 'Invalid token.') {
                    localStorage.removeItem('token');
                    history.push('/login');
                    return;
                }
                toast.error(newFundraiser.detail);
            } else {
                // Redirect to the new fundraiser
                history.replace(`/fundraisers/edit/${newFundraiser?.id}`);
                toast.success(
                    <div>
                        Fundraiser created successfully!{' '}
                        <a
                            href={`${eventsUrl}/event/${newFundraiser?.id}/signup/`}
                            target='_blank'
                        >
                            View your Campaign Fundraiser here!
                        </a>
                    </div>
                );
                // Scroll to top of page
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        }
    };

    const getFundraiser = async (id: string) => {
        const fundraiser = await fetchFundraiser(id, campaignId);
        setFundraiserToEdit(fundraiser);
    };

    useEffect(() => {
        if (id) {
            getFundraiser(id);
        }
    }, [id]);

    const getMyCampaign = async () => {
        try {
            if (user?.is_superuser) {
                return;
            }
            const resp: MyCampaignResponse = await fetchMyCampaign();
            if (resp.detail && resp.detail === 'Invalid token.') {
                // Delete localStorage token
                localStorage.removeItem('token');
                history.push('/login');
            } else {
                campaignDispatch({
                    type: 'set_campaign',
                    payload: resp,
                });
                setLoading(false);
            }
        } catch (e) {
            console.log('getMyCampaign() e: ', e);
            // Show some error here
        }
    };

    useEffect(() => {
        if (isEditing) {
            return;
        }
        if (i_am_organizer && campaign && 'person' in campaign) {
            const { person } = campaign;
            setValue('organizer_first_name', person?.first_name);
            setValue('organizer_last_name', person?.last_name);
            setValue('organizer_email', person?.email);
            setValue('organizer_phone', person?.phone);
        } else {
            setValue('organizer_first_name', '');
            setValue('organizer_last_name', '');
            setValue('organizer_email', '');
            setValue('organizer_phone', '');
        }
    }, [isEditing, campaign, i_am_organizer]);

    useEffect(() => {
        if (fundraiserToEdit && isEditing) {
            // This is the quickest and most legible way to set the checkboxes
            // If there's an event end date, it's multiple day
            let isMultipleDayCheck = fundraiserToEdit.event_end_date
                ? true
                : false;
            let requireRegistrationCheck =
                !!fundraiserToEdit.registration_close_date ||
                !!fundraiserToEdit.registration_items?.length ||
                !!fundraiserToEdit.registration_question;
            reset({
                ...fundraiserToEdit,
                is_multiple_day: isMultipleDayCheck,
                requires_registration: requireRegistrationCheck,
                is_oneday: !isMultipleDayCheck && !fundraiserToEdit.is_ongoing,
                // i_am_organizer: false,
            } as unknown as DefaultEditFundraiserType);
        }
    }, [fundraiserToEdit]);
    // console.log('fundraiserToEdit: ', fundraiserToEdit);

    return (
        <div className='container__inner'>
            <div className='mb-5'>
                <Row>
                    <Col xl={7}>
                        <h1 className='h2'>
                            {fundraiserToEdit ? 'Edit' : 'Add'} Fundraiser
                        </h1>
                        <p>
                            Let's get people excited about your upcoming
                            fundraiser! To get started, tell us a little about
                            the fundraiser page you'd like to create.
                        </p>
                    </Col>
                    <Col
                        xl={5}
                        className='d-none d-md-flex justify-content-xl-end align-items-start'
                    >
                        {fundraiserToEdit && (
                            <Button
                                color='link'
                                className='px-0'
                                onClick={() => history.push('/fundraisers')}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                        )}
                        <Button
                            {...(fundraiserToEdit && {
                                tag: 'a',
                                href: `${eventsUrl}/event/${fundraiserToEdit?.id}/signup/`,
                                target: '_blank',
                            })}
                            {...(!fundraiserToEdit && {
                                onClick: () => history.push('/fundraisers'),
                            })}
                            color='outline-dark'
                        >
                            {fundraiserToEdit ? 'View' : ' Cancel'}
                        </Button>
                        <Button
                            onClick={handleSubmit(onSubmit)}
                            disabled={
                                isSubmitting ||
                                !hasFundraiserLength ||
                                hasErrors
                            }
                            color='primary'
                        >
                            {getButtonText()}
                        </Button>
                    </Col>
                </Row>
            </div>

            <Row>
                <Col lg={7}>
                    <CheckboxInput
                        name='published'
                        label='Display Fundraiser on the Campaign Page'
                        control={control}
                    // value={published}
                    // onChange={() => {
                    //   setValue('published', !published);
                    // }}
                    />
                    <h5>WHAT</h5>
                    <TextInput
                        name='name'
                        label='Fundraiser Title'
                        control={control}
                        type='text'
                        required={true}
                        ariaRequired={true}
                    />
                    <TextInput
                        name='details'
                        label='Description'
                        control={control}
                        type='textarea'
                        required={true}
                        ariaRequired={true}
                    />
                    <Row className='g-3 mb-3 align-items-center'>
                        <Col>
                            <FormGroup>
                                <Label>Featured Image</Label>
                                <FundraiserImageUpload
                                    setValue={setValue}
                                    type='event_picture'
                                    accept={{
                                        'image/*': ['.jpeg', '.jpg', '.png'],
                                    }}
                                    savedUrl={watch('event_picture')}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row className='g-3 mb-3 align-items-center'>
                        <Col xs='6'>
                            <FormGroup>
                                <Label>Fundraiser Flyer</Label>
                                <Input
                                    readOnly={true}
                                    value={getFilename(watch('flyer')) || 'No file selected'}
                                />
                                <FormText>Accepts PNG, JPG, or PDF</FormText>
                            </FormGroup>
                        </Col>
                        <Col>
                            {watch('flyer') ? (
                                <Button
                                    color='link'
                                    className='text-danger fs-5 mt-2 px-0'
                                    onClick={() => {
                                        setValue('flyer', '');
                                    }}
                                >
                                    {renderIcon('close')}
                                </Button>
                            ) : (
                                <Button
                                    color='outline-dark'
                                    onClick={() =>
                                        setShowFlyerPhotoUpload(true)
                                    }
                                >
                                    <span className='me-2'>
                                        {renderIcon('upload')}
                                    </span>
                                    Upload
                                </Button>
                            )}
                        </Col>
                    </Row>
                    {showFlyerPhotoUpload && (
                        <FundraiserFlyerUpload
                            setValue={setValue}
                            type='flyer'
                            accept={{
                                'image/*': ['.jpeg', '.jpg', '.png'],
                                'application/pdf': [],
                            }}
                        />
                    )}
                    {/* <TextInput
            name='event_url'
            label='External Link (Optional)'
            control={control}
            type='url'
            helperText='Add URL if fundraiser tickets or more info is available on a different site. '
          /> */}
                </Col>
                <Col
                    lg={5}
                    className='d-lg-flex justify-content-end align-items-start'
                >
                    {/* <Card
            style={{
              width: '90%',
              background: getDashColor('supporters'),
            }}
          >
            <CardBody>
              <p>Tips for creating a buzz worthy fundraiser:</p>
              <ul>
                <li>One</li>
                <li>Two</li>
                <li>Three</li>
                <li>Four</li>
              </ul>
              <Button
                color='link'
                className='px-0'
                onClick={() => console.log('click')}
              >
                View Example Fundraiser (Helpful Tools)
              </Button>
            </CardBody>
          </Card> */}
                </Col>
            </Row>

            <hr className='my-5' />

            <Row>
                <Col lg={7}>
                    <h5>WHEN</h5>
                    <Label>Fundraiser Length</Label>
                    <FormGroup check inline className='mb-3'>
                        <label className='form-check-label'>
                            One Day
                            <input
                                name='fundraiser_length'
                                id='is_oneday'
                                className='form-check-input'
                                type='radio'
                                checked={is_oneday}
                                onChange={() => {
                                    setValue('is_oneday', !is_oneday);
                                    setValue('is_ongoing', false);
                                    setValue('is_multiple_day', false);
                                }}
                            />
                        </label>
                    </FormGroup>
                    <FormGroup check inline className='mb-3'>
                        <label className='form-check-label'>
                            Multiple Days
                            <input
                                name='fundraiser_length'
                                id='is_multiple_day'
                                className='form-check-input'
                                type='radio'
                                checked={is_multiple_day}
                                onChange={() => {
                                    setValue(
                                        'is_multiple_day',
                                        !is_multiple_day
                                    );
                                    setValue('is_oneday', false);
                                    setValue('is_ongoing', false);
                                }}
                            />
                        </label>
                    </FormGroup>
                    <FormGroup check inline className='mb-3'>
                        <label className='form-check-label'>
                            Ongoing
                            <input
                                name='fundraiser_length'
                                id='is_ongoing'
                                className='form-check-input'
                                type='radio'
                                checked={is_ongoing}
                                onChange={() => {
                                    setValue('is_ongoing', !is_ongoing);
                                    setValue('is_oneday', false);
                                    setValue('is_multiple_day', false);
                                }}
                            />
                        </label>
                    </FormGroup>

                    {!is_ongoing && (
                        <>
                            <Row>
                                <Col sm={4}>
                                    <TextInput
                                        name='event_date'
                                        label={
                                            is_multiple_day
                                                ? 'Start Date'
                                                : 'Date'
                                        }
                                        control={control}
                                        type='date'
                                        required={is_oneday || is_multiple_day}
                                    />

                                    {is_multiple_day && (
                                        <TextInput
                                            name='event_end_date'
                                            label='End date'
                                            control={control}
                                            type='date'
                                            required={is_multiple_day}
                                        />
                                    )}

                                    <TextInput
                                        name='event_time_text'
                                        label='Time'
                                        control={control}
                                        type='text'
                                        // helperText="HH:MM or 'From 2 Till 7'"
                                        required={false}
                                    />
                                </Col>
                            </Row>
                        </>
                    )}

                    {!hasFundraiserLength && (
                        <div
                            className='invalid-feedback'
                            style={{ display: 'block' }}
                        >
                            Must select a fundraiser length
                        </div>
                    )}
                </Col>
            </Row>

            <hr className='my-5' />

            <Row>
                <Col lg={7}>
                    <h5>WHERE</h5>
                    <Row>
                        <Col sm={8}>
                            <CheckboxInput
                                name='is_virtual'
                                label='This is a virtual fundraiser'
                                control={control}
                            // value={is_virtual}
                            // onChange={() => {
                            //   setValue('is_virtual', !is_virtual);
                            // }}
                            />
                            {!is_virtual && (
                                <>
                                    <TextInput
                                        name='location'
                                        label='Fundraiser Location'
                                        control={control}
                                        type='text'
                                    />
                                    <TextInput
                                        name='address_1'
                                        label='Street Address 1'
                                        control={control}
                                        type='text'
                                    />
                                    <TextInput
                                        name='address_2'
                                        label='Street Address 2'
                                        control={control}
                                        type='text'
                                    />
                                    <TextInput
                                        name='city'
                                        label='City'
                                        control={control}
                                        type='text'
                                    />
                                    <Row>
                                        <Col sm={8}>
                                            <FormGroup>
                                                <Label>State</Label>
                                                <Input
                                                    type='select'
                                                    name='state'
                                                    id='state'
                                                    value={watch('state')}
                                                    defaultValue={getValues(
                                                        'state'
                                                    )}
                                                    onChange={(e) =>
                                                        setValue(
                                                            'state',
                                                            e.target.value
                                                        )
                                                    }
                                                >
                                                    <option value={undefined}>
                                                        Select
                                                    </option>
                                                    {Object.entries(states).map(
                                                        (
                                                            [accronym, label],
                                                            i
                                                        ) => (
                                                            <option
                                                                key={i}
                                                                value={accronym}
                                                            >
                                                                {label}
                                                            </option>
                                                        )
                                                    )}
                                                </Input>
                                                {errors.state && (
                                                    <FormFeedback>
                                                        {errors.state?.message}
                                                    </FormFeedback>
                                                )}
                                            </FormGroup>
                                        </Col>
                                        <Col sm={4}>
                                            <TextInput
                                                name='zip_code'
                                                label='Zip Code'
                                                type='zip'
                                                control={control}
                                            />
                                        </Col>
                                    </Row>
                                </>
                            )}
                        </Col>
                    </Row>
                </Col>
            </Row>

            <hr className='my-5' />

            <Row>
                <Col lg={7}>
                    <h5>WHO TO CONTACT</h5>
                    <CheckboxInput
                        name='i_am_organizer'
                        label='I am the organizer'
                        control={control}
                    />
                    <Row>
                        <Col sm={8}>
                            <TextInput
                                name='organizer_first_name'
                                label='Organizer First Name'
                                control={control}
                                type='text'
                                required={false}
                            />
                            <TextInput
                                name='organizer_last_name'
                                label='Organizer Last Name'
                                control={control}
                                type='text'
                                required={false}
                            />
                            <PhoneInput
                                name='organizer_phone'
                                label='Organizer Phone'
                                control={control}
                                required={false}
                            />
                            <TextInput
                                name='organizer_email'
                                label='Organizer Email'
                                control={control}
                                type='email'
                                required={false}
                            />
                        </Col>
                    </Row>
                </Col>
            </Row>

            <hr className='my-5' />

            <Row className='mb-5'>
                <Col lg={7}>
                    <h5>REGISTRATION</h5>

                    <CheckboxInput
                        name='requires_registration'
                        label='This fundraiser requires online registration'
                        control={control}
                        // value={requires_registration}
                        onClick={() => {
                            // setValue('requires_registration', !requires_registration);
                            setTimeout(() => {
                                // amount to scroll is negative to scroll up
                                window.scrollBy({
                                    top: 600,
                                    behavior: 'smooth',
                                });
                            }, 400);
                        }}
                    />

                    {requires_registration && (
                        <>
                            <Row>
                                <Col sm={4}>
                                    <TextInput
                                        name='registration_close_date'
                                        label='Registration Close Date'
                                        control={control}
                                        type='date'
                                        required={!is_ongoing}
                                    />
                                    {/* <TextInput
                    name='maximum_registrants'
                    label='Maximum Number of Registrations'
                    control={control}
                    type='text'
                    helperText='This may be used to limit the amount of tickets that can be sold for a fundraiser'
                  /> */}
                                </Col>
                            </Row>

                            <h5>
                                Registration Options{' '}
                                <span className='required'>*</span>
                            </h5>
                            <FormGroup>
                                <Label>
                                    Use this field to add fundraiser options
                                    such as tickets, sponsorship opportunities,
                                    and more.
                                </Label>
                                {itemFields.map((i, index) => {
                                    return (
                                        <div key={i._id}>
                                            <Row className='align-items-center'>
                                                <Col sm={7}>
                                                    <Controller
                                                        name={`registration_items.${index}.name` as any}
                                                        control={control}
                                                        rules={{ required: true }}
                                                        render={({ field }) => (
                                                            <input
                                                                {...field}
                                                                type='text'
                                                                required
                                                                className='form-control mb-2'
                                                            />
                                                        )}
                                                    />
                                                </Col>
                                                <Col sm={3}>
                                                    <Controller
                                                        name={`registration_items.${index}.price` as any}
                                                        control={control}
                                                        render={({ field }) => (
                                                            <IntlCurrencyInput
                                                                value={field.value}
                                                                defaultValue={field.value}
                                                                max={999999}
                                                                onChange={(
                                                                    e,
                                                                    val,
                                                                    masked
                                                                ) => {
                                                                    field.onChange(val);
                                                                }}
                                                                config={currencyConfig as any}
                                                            />
                                                        )}
                                                    />
                                                </Col>
                                                <Col sm={2}>
                                                    <AiOutlineCloseCircle
                                                        color='danger'
                                                        size={40}
                                                        onClick={() =>
                                                            onRemoveItem(
                                                                i,
                                                                index
                                                            )
                                                        }
                                                    />
                                                </Col>
                                            </Row>
                                            <Row className='mb-1'>
                                                <Col sm={7}>
                                                    {errors?.registration_items?.[index] && (errors.registration_items[index] as any)?.name && (
                                                        <FormFeedback type='invalid' style={{ display: 'block' }}>
                                                            Name is required
                                                        </FormFeedback>
                                                    )}
                                                </Col>
                                                <Col sm={3}></Col>
                                            </Row>
                                        </div>
                                    );
                                })}
                                <Button
                                    color='outline-dark'
                                    onClick={() =>
                                        appendItem({ name: '', price: '' })
                                    }
                                >
                                    <span className='me-2'>
                                        {renderIcon('add')}
                                    </span>
                                    Add Fundraiser Item
                                </Button>
                            </FormGroup>
                            <label>
                                <h5>
                                    <h5>Registration Question (Optional)</h5>
                                </h5>
                            </label>
                            <FormGroup>
                                <Label>
                                    Use this field to request additional details
                                    from registrants, such as their t-shirt size
                                    or meal preferences.
                                </Label>
                                <textarea
                                    {...register('registration_question')}
                                    className='form-control mb-2'
                                />
                            </FormGroup>
                            {/* <h5>Registration Call to Action</h5>
              <FormGroup>
                <Label>
                  Select the label that best describes the action required to
                  confirm fundraiser options during checkout.
                </Label>
                <CheckBox
                  name='call_to_action_1'
                  label='Register Now (default)'
                  value={watch('call_to_action') === 'Register Now'}
                  onChange={() => {
                    setValue('call_to_action', 'Register Now');
                  }}
                />
                <CheckBox
                  name='call_to_action_2'
                  label='Sponsor Now'
                  value={watch('call_to_action') === 'Sponsor Now'}
                  onChange={() => {
                    setValue('call_to_action', 'Sponsor Now');
                  }}
                />
                <CheckBox
                  name='call_to_action_3'
                  label='Buy Now'
                  value={watch('call_to_action') === 'Buy Now'}
                  onChange={() => {
                    setValue('call_to_action', 'Buy Now');
                  }}
                />
              </FormGroup> */}
                            <TextInput
                                name='thank_you_message'
                                label='Enter an optional thank you message that will be shown to the registrants on checkout'
                                control={control}
                                type='text'
                                required={false}
                            />
                        </>
                    )}

                    {/* <hr className='my-5' />
          <Row>
            <Col>
              <h5>PHOTO GALLERY</h5>
            </Col>

            <Col>
              <Button color='link'>Edit Gallery</Button>
            </Col>
          </Row> */}
                </Col>
                <Col lg={5}>
                    {requires_registration && (
                        <Card className='border'>
                            <CardBody>
                                <h4>
                                    Registrations (
                                    {fundraiserToEdit?.registration_total})
                                </h4>
                                <Table className='small mb-4'>
                                    <tbody>
                                        {registrations
                                            .slice(0, 5)
                                            .map((registration, index) => {
                                                const name = `${registration.first_name} ${registration.last_name}`;
                                                return (
                                                    <tr key={index}>
                                                        <td className='ps-0'>
                                                            {name}
                                                        </td>
                                                        <td className='pe-0 fw-bold text-end'>
                                                            {registration.registration_details.map(
                                                                (
                                                                    detail,
                                                                    index
                                                                ) => {
                                                                    return (
                                                                        <div
                                                                            key={
                                                                                index
                                                                            }
                                                                        >
                                                                            {
                                                                                detail
                                                                                    .item
                                                                                    ?.name
                                                                            }{' '}
                                                                            x
                                                                            {
                                                                                detail.quantity
                                                                            }
                                                                        </div>
                                                                    );
                                                                }
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                    </tbody>
                                </Table>
                                <Link
                                    className='btn btn-sm btn-outline-dark'
                                    to={`/fundraisers/registrations/${id}`}
                                >
                                    View All
                                </Link>
                            </CardBody>
                        </Card>
                    )}
                </Col>
            </Row>
            <div
                className='sticky-bottom p-4 bg-white'
                style={{
                    borderTop: '1px solid #0D0106',
                    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.25)',
                }}
            >
                {fundraiserToEdit && (
                    <Button
                        color='link'
                        className='px-0'
                        onClick={() => history.goBack()}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                )}
                <Button
                    {...(fundraiserToEdit && {
                        tag: 'a',
                        href: `${eventsUrl}/event/${fundraiserToEdit?.id}/signup/`,
                        target: '_blank',
                    })}
                    {...(!fundraiserToEdit && {
                        onClick: () => history.push('/fundraisers'),
                    })}
                    color='outline-dark'
                >
                    {fundraiserToEdit ? 'View' : ' Cancel'}
                </Button>
                <Button
                    color='primary'
                    disabled={isSubmitting || !hasFundraiserLength || hasErrors}
                    onClick={() => {
                        handleSubmit(onSubmit, () => {
                            console.log('errors', errors);
                            const errorMessages = Object.entries(errors).map(
                                ([key, value]) => {
                                    return value.message?.replace(
                                        ' is required',
                                        ''
                                    );
                                }
                            );
                            toast.error(
                                `Required fields missing: \n\n` +
                                errorMessages.join('\n')
                            );
                        })();
                    }}
                >
                    {getButtonText()}
                </Button>
            </div>
        </div>
    );
};
